import JSZip from 'jszip';
import {getNotNullishNodes} from '../../forecast-app/shared/util/NotNullPredicate';
import {ExpenseRow} from './ExpenseTypes';
import {trackEvent} from '../../tracking/amplitude/TrackingV2';
import DirectApi from '../../directApi.js';
import {getProjectIndicatorString} from '../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';

export type File = NonNullable<NonNullable<NonNullable<NonNullable<ExpenseRow['files']>['edges']>[0]>['node']>;

type Expense = {
	project: Pick<NonNullable<ExpenseRow['project']>, 'companyProjectId' | 'customProjectId'> | null;
	files: ExpenseRow['files'];
	name: ExpenseRow['name'];
};

const fetchFile = (file: File) =>
	fetch(`${DirectApi.fileSrc(file.id)}?1111`, {
		credentials: 'include', // Send along cookies (sessionKey)
	});

const addFileCount = (fileName: string, number: number) => {
	const pieces = fileName.split('.');
	const hasPieces = pieces.length;
	const firstPart = hasPieces > 1 ? pieces.slice(0, -1).join('.') : fileName;
	const extension = hasPieces > 1 ? '.' + pieces.slice(-1) : '';
	return firstPart + ' (' + number + ')' + extension;
};

const projectPrefix = (expense: Expense) =>
	`${getProjectIndicatorString(expense.project?.companyProjectId, expense.project?.customProjectId)}`;

export const downloadExpenseFiles = async (expenses: Expense[], bulk?: boolean): Promise<void> => {
	const hasFilesToDownload = expenses.some(expense => getNotNullishNodes(expense.files?.edges).length > 0);

	if (!hasFilesToDownload) {
		return;
	}

	const trackEventName = bulk ? 'Bulk Expense Download All Files Button' : 'Expense Actions Download Files';

	if (expenses.length === 1) {
		const expense = expenses[0];
		const files = expense.files?.edges?.map(file => file?.node) || [];

		if (files.length === 1) {
			const file = files[0];
			if (!file) return;
			const fileContent = await fetchFile(file).then(response => response.blob());

			const downloadLink = document.createElement('a');
			downloadLink.href = URL.createObjectURL(fileContent);
			downloadLink.download = `${getProjectIndicatorString(
				expense.project?.companyProjectId,
				expense.project?.customProjectId
			)}_${file.name}`;

			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);

			trackEvent(trackEventName, 'Clicked', {totalExpenses: expenses.length, totalFiles: files.length});
			return;
		}
	}

	const zip = new JSZip();
	const sameProject = expenses.every((expense, index) => {
		return index === 0 || expense.project?.companyProjectId === expenses[index - 1].project?.companyProjectId;
	});

	const fileNameCountMap = new Map();
	const getUniqueFileName = (rawFileName: string) => {
		let fileNameCount = fileNameCountMap.get(rawFileName) || 0;
		const fileName = fileNameCount ? addFileCount(rawFileName, fileNameCount) : rawFileName;
		fileNameCountMap.set(rawFileName, ++fileNameCount);
		return fileName;
	};

	const fetchAndAddToZip = async (file: File, expense: Expense) => {
		const fileContent = await fetchFile(file).then(response => response.arrayBuffer());

		const fileNameProjectPrefix = sameProject ? '' : projectPrefix(expense) + '_';
		const fileNameExpenseName = expense.name ? expense.name.replace(/ /g, '-') + '_' : '';
		const rawFileName = fileNameProjectPrefix + fileNameExpenseName + file.name || 'unnamed-file';

		zip.file(getUniqueFileName(rawFileName), fileContent);
	};

	await Promise.all(
		expenses.map(expense =>
			Promise.all(getNotNullishNodes(expense.files?.edges).map(file => fetchAndAddToZip(file, expense)))
		)
	);

	zip.generateAsync({type: 'blob'}).then(content => {
		const downloadLink = document.createElement('a');
		if (sameProject) {
			downloadLink.download = `${projectPrefix(expenses[0])}_expense_files.zip`;
		} else {
			const timestamp = new Date().toISOString().split('.')[0];
			downloadLink.download = `${timestamp}_expense_files.zip`;
		}
		downloadLink.href = URL.createObjectURL(content);

		document.body.appendChild(downloadLink);
		downloadLink.click();
		document.body.removeChild(downloadLink);

		trackEvent(trackEventName, 'Clicked', {
			totalExpenses: expenses.length,
			totalFiles: zip.files ? Object.keys(zip.files).length : 0,
		});
	});
};
